<template>
	<div class="content-sections">
		<section class="stack">
			<h4 class="sgnl-h6-semibold sgnl-text-color-default">Profile</h4>
			<v-form
				v-if="userStore.currentUser"
				class="stack"
			>
				<div class="input-group">
					<div class="field-label">UserID</div>
					<div class="userid-field">
						<v-text-field
							v-model="userStore.currentUser.userID"
							class="sgnl-font-weight-medium"
							disabled
							hide-details
						/>
						<CopyToClipboardButton
							:value-to-copy="userStore.currentUser.userID"
							value-name="User ID"
						/>
					</div>
				</div>
				<div class="input-group">
					<div class="field-label">E-mail</div>
					<v-text-field
						v-model="userStore.currentUser.email"
						class="sgnl-font-weight-medium"
						disabled
						hide-details
					/>
				</div>
				<div class="input-group">
					<div class="field-label">Name</div>
					<v-text-field
						v-model="editableName"
						class="sgnl-font-weight-medium"
						hide-details
						:loading="nameSaving"
						:disabled="nameSaving"
					/>
				</div>
				<v-fade-transition>
					<v-alert
						v-if="nameError"
						density="compact"
						variant="outlined"
						type="warning"
						border="start"
						height="36px"
						class="sgnl-mt-0 sgnl-paragraph-s-regular sgnl-py-4"
					>
						{{ nameError }}
					</v-alert>
				</v-fade-transition>
				<TooltipButton
					variant="flat"
					color="primary"
					:loading="nameSaving"
					btnText="Save"
					:disabled="!nameHasChanged"
					@click="saveName"
				>
					<template #btnContent>
						<Save16 />
						<span>Save</span>
					</template>
				</TooltipButton>

				<div
					class="input-group"
					v-if="organizationId"
				>
					<div class="field-label">Organization</div>
					<v-tooltip
						location="bottom"
						:disabled="!organizationId"
					>
						<template v-slot:activator="{ props }">
							<v-chip
								v-bind="props"
								variant="outlined"
								class="organization-badge"
							>
								<Events16 />
								<span>{{ organizationName || "Organization" }}</span>
							</v-chip>
						</template>
						<div class="sgnl-paragraph-s-regular sgnl-text-center org-tooltip">
							<div>Your account is part of the organization</div>
							<div class="sgnl-caption-medium">
								{{ organizationName || organizationId }}
							</div>
						</div>
					</v-tooltip>
				</div>
			</v-form>
		</section>

		<section class="stack">
			<h4 class="sgnl-h6-semibold sgnl-text-color-default">Security</h4>
			<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
				Sign out from all browsers and devices where you are currently logged in. Use this if you suspect
				unauthorized access to your account.
			</div>
			<TooltipButton
				@click="openLogoutFromAllDevicesDialog"
				btnText="Sign Out From All Devices"
				variant="outlined"
			/>
			<LogoutFromAllDevicesDialog
				:show-dialog="logoutFromAllDevicesDialog"
				@closeLogoutFromAllDialog="closeLogoutFromAllDevicesDialog"
				:passedEmail="authenticatedUser?.attributes?.email"
			/>
		</section>

		<section class="stack">
			<h4 class="sgnl-h6-semibold sgnl-text-color-default">Delete Account</h4>
			<div class="sgnl-paragraph-s-regular sgnl-text-color-secondary">
				Permanently delete your account and all associated data. This action cannot be undone.
			</div>
			<TooltipButton
				@click="openDeleteAccountDialog"
				btnText="Delete Account"
				variant="outlined"
				color="error"
			/>
			<DeleteAccountDialog
				:show-dialog="deleteAccountDialog"
				@closeDeleteDialog="closeDeleteAccountDialog"
				:passedEmail="authenticatedUser?.attributes?.email"
			/>
		</section>
		<v-snackbar
			v-model="successSnackbar"
			:timeout="4000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="successSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
		<v-snackbar
			v-model="errorSnackbar"
			:timeout="6000"
			class="snackbar-error"
		>
			<div class="snackbar-icon-container snackbar-error">
				<ErrorFilled16 />
			</div>
			{{ errorMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="errorSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script lang="ts">
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton.vue";
import DeleteAccountDialog from "@/components/Settings/DeleteAccountDialog.vue";
import LogoutFromAllDevicesDialog from "@/components/Settings/LogoutFromAllDevicesDialog.vue";
import TooltipButton from "@/components/Common/TooltipButton.vue";

import { defineComponent } from "vue";
import Events16 from "@carbon/icons-vue/es/events/16";
import Save16 from "@carbon/icons-vue/es/save/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";
import { useUserStore } from "@/stores/user";
import { AWSAuthenticatedUserObject } from "@/types/user";

import { sdk } from "@/js/sdk";

type ComponentData = {
	authenticatedUser: undefined | null | AWSAuthenticatedUserObject;
	organizationId: string | null;
	organizationName: string;
	deleteAccountDialog: boolean;
	logoutFromAllDevicesDialog: boolean;
	editableName: string;
	nameSaving: boolean;
	nameError: string;
	successSnackbar: boolean;
	successMessage: string;
	errorSnackbar: boolean;
	errorMessage: string;
};

export default defineComponent({
	name: "SettingsProfile",
	components: {
		CopyToClipboardButton,
		DeleteAccountDialog,
		LogoutFromAllDevicesDialog,
		TooltipButton,
		Events16,
		Save16,
		CheckmarkFilled16,
		Close16,
		ErrorFilled16,
	},
	data: (): ComponentData => ({
		authenticatedUser: undefined,
		organizationId: null,
		organizationName: "",
		deleteAccountDialog: false,
		logoutFromAllDevicesDialog: false,
		editableName: "",
		nameSaving: false,
		nameError: "",
		successSnackbar: false,
		successMessage: "",
		errorSnackbar: false,
		errorMessage: "",
	}),
	setup() {
		const userStore = useUserStore();
		return { userStore };
	},
	computed: {
		nameHasChanged(): boolean {
			return this.editableName !== (this.userStore.currentUser?.name ?? "");
		},
	},
	methods: {
		async saveName() {
			this.nameSaving = true;
			this.nameError = "";
			try {
				await this.userStore.updateUserName(this.editableName);
				this.successMessage = "Name updated successfully.";
				this.successSnackbar = true;
			} catch {
				this.nameError = "Failed to update name. Please try again.";
				this.errorMessage = "Failed to update name. Please try again.";
				this.errorSnackbar = true;
			} finally {
				this.nameSaving = false;
			}
		},
		openDeleteAccountDialog() {
			this.deleteAccountDialog = true;
		},
		closeDeleteAccountDialog() {
			this.deleteAccountDialog = false;
		},
		openLogoutFromAllDevicesDialog() {
			this.logoutFromAllDevicesDialog = true;
		},
		closeLogoutFromAllDevicesDialog() {
			this.logoutFromAllDevicesDialog = false;
		},
		async fetchOrganizationName() {
			if (!this.organizationId) return;
			try {
				const response = await sdk.organizations.get(this.organizationId);
				this.organizationName = response?.Name || "";
			} catch (error) {
				console.error("Failed to fetch organization details:", error);
			}
		},
	},
	async created() {
		this.authenticatedUser = await this.userStore.getCurrentAuthenticatedUserObject();
		this.editableName = this.userStore.currentUser?.name ?? "";
		this.organizationId = await this.userStore.getCurrentUserOrganizationID();
		if (this.organizationId) {
			await this.fetchOrganizationName();
		}
	},
});
</script>

<style lang="scss" scoped>
.stack :deep(.v-text-field) {
	max-width: var(--sgnl-container-343);
}

.stack :deep(.v-field--disabled) {
	opacity: 1;
	border-color: var(--sgnl-default-border-disabled);
	color: var(--sgnl-default-text-disabled);
}

.stack :deep(.v-field--disabled .v-field__outline) {
	--v-field-border-opacity: 1;
	color: var(--sgnl-default-border-disabled);
}

.stack :deep(.v-field--disabled input) {
	color: var(--sgnl-default-text-disabled);
}

.userid-field {
	display: flex;
	align-items: center;
	gap: 0.25rem;
}

.org-tooltip {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}

.organization-badge {
	cursor: help;
	align-self: flex-start;
}
</style>
